/**
 * Startbildschirm-TYPOGRAFIE — eine Auflösung für alle Oberflächen.
 *
 * DAS PROBLEM (notiert bei #459, gebaut 26.08.2026): Der Pro-Editor kann am
 * Startbildschirm Schrift und Größenfaktor der Titelzeile und Unterzeile
 * einstellen (`startHeadlineFont/-Scale`, `startSublineFont/-Scale` an der
 * START-Slide). Diese Regler wirkten NUR auf der Editor-Bühne — Vorschau und
 * Leser zeichnen das Cover aus den Metadaten, in die nie gespiegelt wurde.
 *
 * Gleiches Muster wie beim START-Knopf (./start-button): Spiegelung beim
 * Speichern (app/src/lib/cover-meta), hier das Gegenstück fürs Lesen mit
 * RÜCKFALL auf die Slide für Bestands-Scrollys. VORRANG: Metadaten vor Slide.
 *
 * Die Schriftnamen sind die Standard-Familien des Pro-Editors — der Reader
 * registriert sie bereits als @font-face (readerStandardFonts im Renderer-
 * Paket); eine unbekannte Familie fällt im CSS still auf den Default.
 */

export interface StartTypography {
    /** CSS-Familienname der Titelzeile; leer/undefined = Standard-Look. */
    headlineFont?: string;
    sublineFont?: string;
    /** Faktor auf die responsive Grundgröße (1 = unverändert). */
    headlineScale?: number;
    sublineScale?: number;
    /**
     * Gewählter Schnitt (Schriftwahl mit zwei Spalten, 06.10.2026); fehlt =
     * bisheriger Look (Titel mit eigener Schrift 800, Unterzeile normal).
     */
    headlineWeight?: number;
    sublineWeight?: number;
}

interface StartSlideLike {
    from?: number;
    startHeadlineFont?: unknown;
    startSublineFont?: unknown;
    startHeadlineScale?: unknown;
    startSublineScale?: unknown;
    startHeadlineWeight?: unknown;
    startSublineWeight?: unknown;
}

function schrift(wert: unknown): string | undefined {
    return typeof wert === "string" && wert.trim() ? wert.trim() : undefined;
}

function schnitt(wert: unknown): number | undefined {
    return typeof wert === "number" && Number.isFinite(wert) && wert >= 100 && wert <= 900
        ? Math.round(wert)
        : undefined;
}

function faktor(wert: unknown): number | undefined {
    return typeof wert === "number" && Number.isFinite(wert) && wert > 0 && wert !== 1
        ? wert
        : undefined;
}

/**
 * Typografie des Startbildschirms auflösen (Metadaten vor START-Slide).
 * Die START-Slide ist die bei `from === 0` — dieselbe Regel wie beim
 * Knopf-Bild (nicht „die erste im Array").
 */
export function resolveStartTypography(
    metadata:
        | {
              startHeadlineFont?: unknown;
              startSublineFont?: unknown;
              startHeadlineScale?: unknown;
              startSublineScale?: unknown;
              startHeadlineWeight?: unknown;
              startSublineWeight?: unknown;
          }
        | null
        | undefined,
    slides: ReadonlyArray<StartSlideLike | null | undefined> | null | undefined,
): StartTypography | undefined {
    const start = Array.isArray(slides) ? slides.find((s) => s && s.from === 0) : undefined;
    const ergebnis: StartTypography = {
        headlineFont: schrift(metadata?.startHeadlineFont) ?? schrift(start?.startHeadlineFont),
        sublineFont: schrift(metadata?.startSublineFont) ?? schrift(start?.startSublineFont),
        headlineScale: faktor(metadata?.startHeadlineScale) ?? faktor(start?.startHeadlineScale),
        sublineScale: faktor(metadata?.startSublineScale) ?? faktor(start?.startSublineScale),
        headlineWeight: schnitt(metadata?.startHeadlineWeight) ?? schnitt(start?.startHeadlineWeight),
        sublineWeight: schnitt(metadata?.startSublineWeight) ?? schnitt(start?.startSublineWeight),
    };
    return ergebnis.headlineFont ||
        ergebnis.sublineFont ||
        ergebnis.headlineScale !== undefined ||
        ergebnis.sublineScale !== undefined ||
        ergebnis.headlineWeight !== undefined ||
        ergebnis.sublineWeight !== undefined
        ? ergebnis
        : undefined;
}
